import { Component, OnInit, Output, EventEmitter, Input } from '@angular/core';
import { AuthService } from 'src/app/services/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { Store } from '@ngxs/store';
import { SharedService } from 'src/app/services/shared.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { timer, BehaviorSubject, NEVER } from 'rxjs';
import { map, switchMap, takeWhile } from 'rxjs/operators';
import { FavoriteService } from 'src/app/services/favorite.service';
import { Patterns, setTimeout$, Utility } from 'src/app/core/common';
import { AuthStateModel, CheckConfirmAuthentication, ConfirmAuthentication, Login } from 'src/app/states/auth.state';
import { ToastrService } from 'ngx-toastr';
import { RecaptchaHeader, RecaptchaService } from 'src/app/services/recaptcha.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  Utility = Utility;

  constructor(private authService: AuthService, private store: Store, private router: Router,
    public sharedService: SharedService, private route: ActivatedRoute, private spinner: SpinnerService,
    private recaptchaService: RecaptchaService, private favoriteService: FavoriteService, private toastr: ToastrService) { }

  patterns = Patterns;
  get isMainComponent() { return this.route.component == <any>this.constructor; }

  headers: RecaptchaHeader;
  @Input() mobile: string;
  @Input() registerType: 'Seeker' | 'Employer' | undefined;
  password: string;
  authCode: string;
  newUser: boolean;
  authSent: boolean;
  info = {
    firstname: '',
    lastname: '',
    name: '',
    rt: undefined
  };
  checkConfirmCode: string;

  resendDelayTime = 60;
  resendRemain;
  checkConfirmDelayTime = 5;
  checkConfirmRemain;

  @Input('login-with-password') withPassword = false;

  @Output() auth = new EventEmitter<string>();
  @Output() login = new EventEmitter<boolean>();
  @Input() inline = false;
  @Input() flat = false;
  @Input('show-name-input') showNameInput = true;
  @Input() intro = 'برای ادامه شماره موبایل‌تون رو وارد کنید.';
  @Input() heading = 'ورود';
  @Input() rb: string;
  @Input() loginText = 'ثبت شماره موبایل';
  @Input() autoFocus = true;
  async ngOnInit() {
    if (this.isMainComponent) this.sharedService.setNoIndex();
    if (!this.sharedService.isBrowser) return;

    const params = this.route.snapshot.params;
    const rb64 = params['rb64'];
    this.rb = (rb64 ? atob(decodeURIComponent(rb64)) : params['rb']) || this.rb;

    if (!this.inline && params['t'] == 'pwd') this.withPassword = true;

    await this.makeReady();
    const mobile = params['mobile'];
    if (mobile) {
      this.mobile = mobile;
      await this.readyCode();
      this.authCode = params['code'];
      if (this.authCode.length >= 4) { this.confirm(); }
    }
  }

  async makeReady() {
    delete this.authSent;
    this.headers = await this.recaptchaService.header('authenticate');
    this.authSent = false;
    this.stopCheckConfirm();
  }

  private checkConfirm$: any;
  ngOnDestroy(): void {
    this.stopCheckConfirm();
  }

  async startCheckConfirm() {
    this.stopCheckConfirm();
    await this.checkConfirm();
    this.checkConfirm$ = setInterval(async () => {
      await this.checkConfirm();
    }, 5000);
  }

  private async checkConfirm() {
    const response = await this.store.dispatch(new CheckConfirmAuthentication(this.mobile, this.checkConfirmCode, this.info)).toPromise();
    const auth = response?.auth as AuthStateModel;
    this.checkConfirmCode = auth?.confirmCode;
    await this.afterLogin(true);
  }

  private stopCheckConfirm() {
    clearInterval(this.checkConfirm$);
    delete this.checkConfirmCode;
  }

  async authenticate(newHeader = false) {
    this.spinner.show('login');
    if (this.withPassword) {
      await this.store.dispatch(new Login(this.mobile, this.password)).toPromise();
      await this.afterLogin();
    }
    else {
      var useOtp = this.sharedService.waitForOtp(1, (otp: any) => {
        this.authCode = otp.code;
        if (this.authCode && (!this.newUser || !this.showNameInput))
          this.confirm();
        else setTimeout$(() => this.focus());
      });
      this.newUser = !(await this.authService.authenticate(this.mobile, useOtp, newHeader ? null : this.headers));
      await this.readyCode();
      this.auth.emit(this.mobile);
    }
    this.spinner.hide('login');
    this.resetDelay();
  }

  private async readyCode() {
    this.headers = await this.recaptchaService.header('confirm');
    this.authSent = true;
    delete this.authCode;
    setTimeout$(() => this.focus());
  }

  private focus() {
    const focusElement = document.getElementById(this.showNameInput && this.newUser ? 'name' : 'auth-code');
    if (focusElement) { focusElement.focus(); }
  }

  private resetDelay() {
    this.resendRemain = new BehaviorSubject(true).pipe(switchMap((running: boolean) => {
      return running ? timer(0, 1000) : NEVER;
    }), map((t: number) => this.resendDelayTime - t), takeWhile(t => t >= 0));

    this.checkConfirmRemain = new BehaviorSubject(true).pipe(switchMap((running: boolean) => {
      return running ? timer(0, 1000) : NEVER;
    }), map((t: number) => this.checkConfirmDelayTime - t), takeWhile(t => t >= 0));
  }

  async confirm() {
    this.spinner.show('login');
    this.info.rt = this.registerType ? this.registerType : undefined;
    if (this.info.firstname || this.info.lastname)
      this.info.name = (this.info.firstname?.trim() || '') + ' ' + (this.info.lastname?.trim() || '');

    await this.store.dispatch(new ConfirmAuthentication(this.mobile, this.authCode, this.info, this.headers)).toPromise();
    await this.afterLogin();
    this.spinner.hide('login');
  }

  private async afterLogin(silent = false) {
    // if (!this.inline) this.sharedService.scrollToTop();
    let user = this.store.selectSnapshot(state => state.auth.user);
    if (!user) {
      if (!silent) this.toastr.error('کد وارد شده اشتباه است. دوباره امتحان کنید.');
      return;
    }

    this.stopCheckConfirm();
    this.login.emit(true);
    await this.favoriteService.sync();
    this.favoriteService.favorites = null;

    if (this.rb) {
      this.router.navigateByUrl(this.rb);
      return;
    }
    if (!this.inline) {
      if (document.referrer) {
        const referrer = new URL(document.referrer);
        if (referrer.host == location.host) {
          this.router.navigateByUrl(referrer.pathname + referrer.search);
          return;
        }
      }
      if (user.Publisher || user.Agent || user.Operator || user.Karpasha)
        this.router.navigateByUrl('/');
      else if (user.Employer)
        this.router.navigateByUrl('/hire');
      else if (user.Seeker?.IsAcceptable)
        this.router.navigateByUrl('/job');
      else
        this.router.navigateByUrl('/seeker/karpasha');
    }

  }

  smsReceiver: string = '9890009601';//983000616100
}
